/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */

import {
  Director,
  Stage,
  StageHelper,
  DefaultCaps,
  DefaultNavigators,
  CapNames,
  SceneCompass,
} from '@sick-davinci/glow';
import { CrownViewCapData, CrownMeshCap } from '@sick-davinci/glow-crown';
import {
  CrownConnectionProxy,
  CrownProtocolJSON,
} from '@sick-davinci/device-communication';
import { Vector3 } from 'three';

/**
 * Demo:    CrownMesh
 * Description:
 *   This demo application displays a CrownMesh (offline)
 * Features:
 *  - Orbit navigation
 *  - Capability handling (CrowhMesh)
 *  - Scene compass
 *  - ViewCrown / Proxy (offline) connection
 */
export const run = ((viewParent: HTMLElement) => {

  // ** setup the stage *
  const desc = StageHelper.getDefaultSetup();
  desc.cameras[0].fov = 30;
  desc.cameras[0].from = [42, 0, 10];
  desc.cameras[0].to = [0, 0, 10];
  desc.cameras[0].up = [-1, 0, 0];
  desc.cameras[0].near = 0;
  desc.cameras[0].far = 20000;
  const stage = new Stage(viewParent, desc);

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'default', {rotateAround: {
    up: new Vector3(0, 0, 1),
  }});

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Start displaying the scene
  stage.start();

  // * Setup the device connection.
  // * In this case it is a proxy connection which simulates establishing the connection to the device so CrownView will work *
  const cc = new CrownConnectionProxy((json) => {
    const data = JSON.parse(json);  // this implementation only works for JSON
    if (!!data && !!data.header && !!data.header.function) {
      const func = data.header.function;
      switch (func) {
        case 'View/Present/create': // create the View.Present handle
        cc.putObj(JSON.stringify({ header: { status: 0 }, data: { handle: { type: 'handle', id: 0 } } }));
        break;
        case 'View/Present/setID':  // set the view ID
        cc.putObj(JSON.stringify({ header: { status: 0 } }));
        break;
        case 'View/Present/register': // register to send evens
        cc.putObj(JSON.stringify({ header: { status: 0 }, data: { success: true } }));
        break;
        default: break;
      }
    }
  });

  const cp = new CrownProtocolJSON();
  const view = new CrownViewCapData(cc, cp, '3d');
  const director = new Director(stage);
  DefaultCaps.init(director); // addCapability for all default caps
  director.addCapability(new CrownMeshCap());

  // * setup lights so there is smth to see
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0xffffff } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'point', color: 0xff8844 }, orientation: { position: [0, 400, -50] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'point', color: 0x2244ff }, orientation: { position: [0, -60, 50] } });

  // * Set all necessary callback functions for the device connection
  view.onPresentRemove((name) => {
    const objectId = stage.getObjectIdByName(name);
    stage.clearObject(objectId);
  });

  view.onPresentClear(() => {
    stage.clearAllScenes();
  });

  view.onPresentAddCapData((capData) => {
    director.handleCapData(capData);
  });

  // * load and send a stored mesh data to the crown connection (usually this comes from a device)
  setTimeout(() => {
    fetch('./resources/head.json')
      .then((response) => response.text())
      .then((mesh) => cc.putObj(mesh));
  }, 100);
});
